import { useMemo } from 'react';
import { CBadge } from '@coreui/react';
import DataTable from 'components/common/DataTable';
import { useIntl, FormattedMessage } from 'react-intl';
import { cilBan } from '@coreui/icons';
import CIcon from '@coreui/icons-react';
import { formatDate } from 'utils/helpers';
import Pagination from 'components/common/Pagination';
import type { TContactMessages } from 'hooks/contact/useGetMessages';
import requireValue from 'utils/requireValue';
import s from './MessagesTable.module.scss';

/** A row of the messages table. */
export interface IMessageRow {
  id: string;
  email: string;
  name: string;
  date: string;
  responded: boolean | null;
  _classes: string | false | undefined;
  message: string;
  fullMessage: string;
}

interface IMessagesTableProps {
  onRowClick: (message: IMessageRow) => Promise<void>;
  messages: TContactMessages;
  changePage: (page: number) => void;
  currentPage: number;
  total: number | null;
}

const getBadge = (status: boolean | null) => {
  switch (status) {
    case true:
      return 'success';
    case false:
      return 'warning';
    default:
      return 'primary';
  }
};

const NoItemsView = () => (
  <div className={s.noItemsView}>
    <div className={s.text}>
      <FormattedMessage id="messages.empty" />
    </div>
    <CIcon icon={cilBan} size="xxl" />
  </div>
);

const formatUserMessage = (message: string) =>
  message.length > 100 ? `${message.slice(0, 110)}...` : message;

const MessagesTable = ({
  onRowClick,
  messages,
  changePage,
  currentPage,
  total,
}: IMessagesTableProps) => {
  const { formatMessage: t } = useIntl();

  const fields = useMemo(
    () => [
      { key: 'name', label: t({ id: 'messages.name' }) },
      { key: 'message', label: t({ id: 'messages.message' }) },
      { key: 'date', label: t({ id: 'messages.date' }) },
      { key: 'responded', label: t({ id: 'messages.responded' }) },
    ],
    [t],
  );

  const items = useMemo(
    () =>
      // A missing list or item throws, as it did.
      requireValue(messages, 'map').map((item): IMessageRow => {
        const { _id, status, created_at, message, email, sender } = requireValue(item, '_id');
        return {
          id: _id,
          email,
          name: sender,
          date: formatDate(created_at),
          responded: status.responded,
          // Bug: MessagesTable.module.scss has no `striped` class, so unread rows get no class; kept as it was.
          _classes: !status.read && s.striped,
          message: formatUserMessage(message),
          fullMessage: message,
        };
      }),
    [messages],
  );

  return (
    <>
      <DataTable
        clickableRows
        noItemsViewSlot={<NoItemsView />}
        onRowClick={(message: IMessageRow) => onRowClick(message)}
        addTableClasses={s.messageTable}
        hover
        items={items}
        fields={fields}
        size="sm"
        scopedSlots={{
          responded: (item: IMessageRow) => (
            <td>
              <CBadge color={getBadge(item.responded)}>
                {item.responded
                  ? t({ id: 'messages.responded' })
                  : t({ id: 'messages.unresponded' })}
              </CBadge>
            </td>
          ),
        }}
      />
      <Pagination {...{ changePage, currentPage, total }} />
    </>
  );
};

export default MessagesTable;
